import { useMemo } from 'react';
import { useAppSelector } from '../../../app/hooks';
import { selectCurrFeature, selectCurrMeasure } from '../../select/selectSlice';
import {
  ScanDataEnum,
  ScanDataMeasureEnum,
  convertMeasureCubeRanchToScanDataBlock,
} from '../scanDataDefinitions';

export const allowedMeasures: (ScanDataEnum | ScanDataMeasureEnum)[] = [
  ScanDataEnum.count_per_meter,
  ScanDataEnum.estimated_count,
  ScanDataEnum.detection_length,
  ScanDataEnum.detection_area,
  ScanDataEnum.detection_id,
  ScanDataEnum.color_hsv,
];

/**
 * Hook to determine the interval and measure based on the current measure selected in the application.
 * If interval_col or measure_col are present in allowedMeasures, they are used;
 * otherwise, the first item in allowedMeasures is used as a fallback.
 *
 * @returns An object containing the determined interval and measure.
 */
export function useGetIntervalAndMeasure(): {
  interval: ScanDataEnum | ScanDataMeasureEnum;
  measure: ScanDataEnum | ScanDataMeasureEnum;
} {
  // Getting the current measure from the application state
  const currMeasure = useAppSelector(selectCurrMeasure);
  const currFeature = useAppSelector(selectCurrFeature);
  // Using useMemo to compute the interval and measure values only when currMeasure changes
  const { interval, measure } = useMemo(() => {
    const currScanDataMeasure =
      convertMeasureCubeRanchToScanDataBlock(currMeasure);

    // Defining interval_col and measure_col based on the current measure
    const interval_col = currScanDataMeasure;
    const measure_col = currScanDataMeasure || ScanDataEnum.detection_id;

    if (!interval_col || !measure_col) {
      return {
        interval: currScanDataMeasure || allowedMeasures[0],
        measure: currScanDataMeasure || allowedMeasures[0],
      };
    }
    // Determining the interval - if interval_col is within allowedMeasures, use it; otherwise, fallback to the first item in allowedMeasures
    const interval = allowedMeasures.includes(interval_col)
      ? interval_col
      : allowedMeasures[0];

    // Determining the measure - if measure_col is within allowedMeasures, use it; otherwise, fallback to the first item in allowedMeasures
    const measure = allowedMeasures.includes(measure_col)
      ? measure_col
      : allowedMeasures[0];

    // Returning the determined interval and measure
    return { interval, measure };
  }, [currMeasure, currFeature]); // Dependencies for useMemo

  // Returning the determined interval and measure
  return { interval, measure };
}
